import React, { useCallback, useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useDataProvider,
  useNotify,
  ReferenceInput,
  AutocompleteInput,
  SimpleForm,
  Button,
} from "react-admin";
import { Box, CircularProgress } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { FeaturedStudioClass } from "types/graphql";
import { FeaturedClassCard } from "./FeaturedClassCard";
import { RESOURCE_TYPE as RT } from "../../../../lib/constants/resourceType";

type FilterParams = {
  title?: string;
  classId?: string;
  excludeIds?: string[];
};

export function FeaturedClassesList() {
  const [items, setItems] = useState<FeaturedStudioClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [removingIds, setRemovingIds] = useState<string[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Fetch featured classes on mount
  useEffect(() => {
    const fetchFeaturedClasses = async () => {
      try {
        const { data } = await dataProvider.getFeaturedClasses(RT.FEATURED, {});
        if (Array.isArray(data)) {
          setItems(data);
        } else {
          setItems([]);
        }
      } catch (error) {
        notify("Error loading featured classes", "error");
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedClasses();
  }, [dataProvider, notify]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        setItems(newItems);

        try {
          await dataProvider.updateFeaturedClassesOrder(RT.FEATURED, {
            items: newItems.map((item) => item.id),
          });
        } catch (error) {
          notify("Error updating order", "error");
        }
      }
    },
    [items, dataProvider, notify]
  );

  const handleAddStudioClass = useCallback(async () => {
    if (!selectedClassId || selectedClassId === "") {
      notify("Please select a class", "warning");
      return;
    }

    setIsAdding(true);
    try {
      await dataProvider.addFeaturedStudioClass(RT.FEATURED, {
        id: selectedClassId,
      });
      notify("Class added to featured", "success");
      // Refresh the list
      const { data } = await dataProvider.getFeaturedClasses(RT.FEATURED, {});
      if (Array.isArray(data)) {
        setItems(data);
      }
      // Reset selection
      setSelectedClassId("");
    } catch (error) {
      notify("Error adding class", "error");
    } finally {
      setIsAdding(false);
    }
  }, [dataProvider, notify, selectedClassId]);

  const handleRemoveStudioClass = useCallback(
    async (classId: string) => {
      setRemovingIds((prev) => [...prev, classId]);
      try {
        await dataProvider.removeFeaturedStudioClass(RT.FEATURED, {
          id: classId,
        });
        notify("Class removed from featured", "success");
        setItems((prev) => prev.filter((item) => item.id !== classId));
      } catch (error) {
        notify("Error removing class", "error");
      } finally {
        setRemovingIds((prev) => prev.filter((id) => id !== classId));
      }
    },
    [dataProvider, notify]
  );

  const filterToQuery = (searchText: string): FilterParams => {
    const filter: FilterParams = {};

    // Get list of already featured class IDs to exclude
    const excludeIds = items.map((item) => item.studioClass.id);

    // Check if searchText is a valid integer
    const parsedId = parseInt(searchText);
    if (!Number.isNaN(parsedId) && parsedId.toString() === searchText) {
      filter.classId = searchText;
    } else {
      filter.title = searchText;
    }

    // Add exclusion filter
    filter.excludeIds = excludeIds;

    return filter;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} gridGap={8}>
        <SimpleForm
          toolbar={<></>}
          onSubmit={() => {}}
          style={{ width: "100%" }}
        >
          <Box
            display="flex"
            alignItems="center"
            style={{ gap: 8, width: "100%" }}
          >
            <ReferenceInput
              label="Search Classes"
              source="classId"
              reference={RT.CLASSES}
              filter={{ hidden: false }}
              sort={{ field: "createdAt", order: "DESC" }}
              filterToQuery={filterToQuery}
              style={{ flex: 1 }}
              disabled={isAdding}
            >
              <AutocompleteInput
                optionText={(record: any) =>
                  `${record.hidden ? "[HIDDEN]" : ""} ${
                    record.title
                  } (Class ID: ${record.classId})`
                }
                fullWidth
                onSelect={(record: any) => {
                  setSelectedClassId(record?.uuid || "");
                }}
              />
            </ReferenceInput>
            <Button
              label={isAdding ? "Adding..." : "Add Featured Class"}
              onClick={handleAddStudioClass}
              disabled={!selectedClassId || isAdding}
              variant="contained"
              color="primary"
              startIcon={
                isAdding ? <CircularProgress size={20} /> : <AddIcon />
              }
            />
          </Box>
        </SimpleForm>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((item) => (
            <FeaturedClassCard
              key={item.id}
              id={item.id}
              item={item.studioClass}
              onRemove={() => handleRemoveStudioClass(item.id)}
              isRemoving={removingIds.includes(item.id)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
}
